import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TimeZoneCard } from "@ozpanel/kit";
import { Button, Section, sound } from "@ozpanel/ui";
import { useMailboxApi } from "./api";

const SOUND_THEMES = ["default", "chime", "bell", "none"] as const;
const SOUND_LABELS: Record<string, string> = { default: "Default", chime: "Chime", bell: "Bell", none: "No sound" };

// Settings: what this app sounds like and how it shows times. The default webmail client is chosen
// on the Mail page, beside the clients themselves — one place for it, not two.
export function SettingsPanel() {
  const api = useMailboxApi();
  const qc = useQueryClient();
  const soundPref = useQuery({ queryKey: ["sound"], queryFn: api.getSound });
  const setTheme = useMutation({
    mutationFn: (theme: string) => api.setSound(theme),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sound"] }),
  });

  return (
    <div className="space-y-4">
      <Section title="New-mail sound">
        {/* Whose choice is in force, always said: an empty choice FOLLOWS the domain's. */}
        <p className="text-[13px] text-muted">
          {soundPref.data?.own ? "Your own choice." : "Following your domain's choice."}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {SOUND_THEMES.map((theme) => (
            <Button
              key={theme}
              variant={soundPref.data?.effective === theme ? "accent" : "default"}
              onClick={() => {
                setTheme.mutate(theme);
                sound.play(theme);
              }}
              disabled={setTheme.isPending}
            >
              {SOUND_LABELS[theme] ?? theme}
            </Button>
          ))}
          {soundPref.data?.own && (
            <Button variant="ghost" onClick={() => setTheme.mutate("")} disabled={setTheme.isPending}>
              Follow my domain
            </Button>
          )}
        </div>
        {!sound.isUnlocked() && (
          <p className="mt-2 text-[12.5px] text-warn">Tap anywhere on the page first — browsers block sound until you do.</p>
        )}
      </Section>
      <TimeZoneCard />
    </div>
  );
}
